import type { GeneratedContentPage } from '#layers/spaces/utils/content-pages';
import { useI18n } from 'vue-i18n';
import { randomUuid } from '#layers/base/public';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { useContentPages } from '#layers/spaces/composables/use-content-pages';
import { usePageActions } from '#layers/spaces/composables/use-page-actions';
import { createContentDetailBlockRenderData, findGeneratedContentPage, getContentPageOverride, getContentPageOverrideId } from '#layers/spaces/utils/content-pages';
import { blockRenderDataFactory } from '#layers/spaces/utils/space-factory';
import { useToastResultHandling } from '#layers/ui/public';

export function useContentPageCustomization() {
	const { t } = useI18n();
	const store = useWorkspaceEntityStore();
	const { editPage } = usePageActions();
	const { generatedContentPages } = useContentPages();

	const { onError } = useToastResultHandling({
		error: {
			title: t('page.customize_page_error'),
		},
	});

	return {
		customizeContentDetailPage,
	};

	async function customizeContentDetailPage(collectionId: NonNullable<GeneratedContentPage['collectionId']>) {
		try {
			const generatedPage = findGeneratedContentPage(generatedContentPages.value, {
				collectionId,
				type: 'detail',
				intent: 'edit',
			});

			if (!generatedPage) {
				return;
			}

			const overridePageId = getContentPageOverrideId(generatedPage);
			const overridePage = getContentPageOverride(store, generatedPage);

			const customPage = overridePage ?? await store.MonospacePage.create({
				id: randomUuid(),
				title: generatedPage.title,
				route: `${generatedPage.route}-custom`,
				icon: generatedPage.icon,
				iconColor: generatedPage.iconColor,
				isFullWidth: generatedPage.isFullWidth,
				spaceId: null,
				sectionId: generatedPage.sectionId,
				type: generatedPage.type,
				collectionId: generatedPage.collectionId,
				intent: generatedPage.intent,
				overridePage: overridePageId,
				sort: generatedPage.sort,
				isHidden: generatedPage.isHidden,
				description: generatedPage.description,
			});

			const customPageBlock = await store.MonospaceBlock.findFirst({
				filter: {
					pageId: { _eq: customPage.id },
				},
				fetchPolicy: 'fetch-only',
			});

			if (!customPageBlock) {
				await store.MonospaceBlock.create({
					id: randomUuid(),
					name: 'Page Block',
					category: 'page',
					pageId: customPage.id,
					renderData: blockRenderDataFactory(createContentDetailBlockRenderData()),
				});
			}

			await editPage(customPage.id, {
				saveCurrentPath: true,
			});
		}
		catch (error) {
			onError(error);
		}
	}
}
